import React from 'react'
import { ErrorMessage, Field, useFormikContext } from 'formik'
import * as yup from 'yup'
import { InputField, RadioGroupField } from 'components'
import { DamagePickers } from './DamagePickers'
import { Legend } from '../Legend'
import { Fieldset } from 'components/Layout/Fieldset'
import { FormDamageReport } from '../types'
import { DamageIllustrations } from './DamageIllustrations'
import { useTranslation } from 'react-i18next'

const damageSchema = yup.object({
  bumperFront: yup.boolean(),
  bumperBack: yup.boolean(),
  fenderLeft: yup.boolean(),
  fenderRight: yup.boolean(),
  engineCooler: yup.boolean(),
  engineHood: yup.boolean(),
  frontDoorLeft: yup.boolean(),
  frontDoorRight: yup.boolean(),
  sidePanelOrDoorLeft: yup.boolean(),
  sidePanelOrDoorRight: yup.boolean(),
  tailgate: yup.boolean(),
  tires: yup.object().shape({
    frontLeft: yup.boolean(),
    frontRight: yup.boolean(),
    rearLeft: yup.boolean(),
    rearRight: yup.boolean(),
  }),
  hadWindow: yup.boolean(),
  window: yup.string().when('hadWindow', {
    is: true,
    then: yup
      .string()
      .required(
        'pages.damageReports.damageReportForm.damageFields.validation.window'
      ),
    otherwise: yup.string(),
  }),
  hadOther: yup.boolean(),
  other: yup.string().when('hadOther', {
    is: true,
    then: yup
      .string()
      .required(
        'pages.damageReports.damageReportForm.damageFields.validation.other'
      ),
    otherwise: yup.string(),
  }),
})

type NestedBools = { [key: string]: NestedBools | boolean | any }
const someValues = (value: NestedBools): boolean =>
  Object.values(value).some((v) =>
    typeof v === 'object' ? someValues(v) : !!v
  )

export const validationSchema = yup.object({
  damage: damageSchema
    .required()
    .test(
      'at-least-one',
      'pages.damageReports.damageReportForm.damageFields.validation.damage',
      (value) => (value ? someValues(value) : true)
    ),
  multipleVehicles: yup
    .boolean()
    .required(
      'pages.damageReports.damageReportForm.damageFields.validation.option'
    ),
  otherVehicleLicensePlate: yup.string().when('multipleVehicles', {
    is: true,
    then: yup
      .string()
      .required(
        'pages.damageReports.damageReportForm.damageFields.validation.vehiclePlate'
      ),
    otherwise: yup.string(),
  }),
  otherVehicleDamage: yup.object().when('multipleVehicles', {
    is: true,
    then: damageSchema
      .required(
        'pages.damageReports.damageReportForm.damageFields.validation.damage'
      )
      .test(
        'at-least-one',
        'pages.damageReports.damageReportForm.damageFields.validation.damage',
        (value) => (value ? someValues(value) : true)
      ),
    otherwise: damageSchema,
  }),
  hadTrailer: yup
    .boolean()
    .required(
      'pages.damageReports.damageReportForm.damageFields.validation.option'
    ),
  hadOtherDamages: yup
    .boolean()
    .required(
      'pages.damageReports.damageReportForm.damageFields.validation.option'
    ),
  otherDamages: yup.string().when('hadOtherDamages', {
    is: true,
    then: yup
      .string()
      .required(
        'pages.damageReports.damageReportForm.damageFields.validation.otherDamage'
      ),
    otherwise: yup.string(),
  }),
})

export const DamageFields: React.FunctionComponent = () => {
  const {
    values: { hadOtherDamages, multipleVehicles, damage, otherVehicleDamage },
    setFieldValue,
  } = useFormikContext<FormDamageReport>()
  const { t } = useTranslation()
  const yesNoOptions = [
    { label: t('global.yes'), value: true },
    { label: t('global.no'), value: false },
  ]
  return (
    <>
      <Fieldset large>
        <Legend>
          {t('pages.damageReports.damageReportForm.damageFields.what')}
        </Legend>

        <DamageIllustrations
          prefix="damage"
          values={damage}
          setFieldValue={setFieldValue}
        />
        <DamagePickers prefix="damage" values={damage} />
        <ErrorMessage name="damage">
          {
            /* Don't print messages for values nested in the damage object */
            (msg) => (typeof msg === 'string' ? t(msg) : null)
          }
        </ErrorMessage>
      </Fieldset>

      <Fieldset large>
        <Legend>
          {t('pages.damageReports.damageReportForm.damageFields.otherVehicle')}
        </Legend>
        <RadioGroupField
          name="multipleVehicles"
          radios={yesNoOptions}
          inline
          required
        />

        {multipleVehicles && (
          <>
            <Field
              component={InputField}
              id="otherVehicleLicensePlate"
              name="otherVehicleLicensePlate"
              label={t(
                'pages.damageReports.damageReportForm.damageFields.officialPlate'
              )}
              required
            />

            <Fieldset>
              <Legend>
                {t(
                  'pages.damageReports.damageReportForm.damageFields.otherVehicleDamage'
                )}
              </Legend>

              <DamageIllustrations
                prefix="otherVehicleDamage"
                values={otherVehicleDamage}
                setFieldValue={setFieldValue}
              />
              <DamagePickers
                prefix="otherVehicleDamage"
                values={otherVehicleDamage}
              />

              <ErrorMessage name="otherVehicleDamage">
                {
                  /* Don't print messages for values nested in the damage object */
                  (msg) => (typeof msg === 'string' ? t(msg) : null)
                }
              </ErrorMessage>
            </Fieldset>
          </>
        )}
      </Fieldset>

      <Fieldset>
        <Legend>
          {t('pages.damageReports.damageReportForm.damageFields.trailer')}
        </Legend>
        <RadioGroupField
          name="hadTrailer"
          radios={yesNoOptions}
          inline
          required
        />
      </Fieldset>

      <Fieldset>
        <Legend>
          {t('pages.damageReports.damageReportForm.damageFields.moreDamage')}
        </Legend>
        <RadioGroupField
          name="hadOtherDamages"
          radios={yesNoOptions}
          inline
          required
        />

        {hadOtherDamages && (
          <Field
            component={InputField}
            id="otherDamages"
            name="otherDamages"
            label={t(
              'pages.damageReports.damageReportForm.damageFields.whatLabel'
            )}
            required
          />
        )}
      </Fieldset>
    </>
  )
}
